/**
 * Reusable button + flow for : pick a receipt photo -> open crop modal
 * (auto-detect document corners with Gemini) -> warp image -> send to OCR ->
 * call the provided onResult(parsed) so the parent form can auto-fill its
 * fields. Also exposes the warped File via onFilePicked(file) so the parent
 * can persist it as the expense attachment when submitting the form.
 *
 * Used by both ExpensesPage (admin) and EmployeeExpensesPage.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Sparkle, X } from '@phosphor-icons/react';
import DocumentCropModal from './DocumentCropModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReceiptOcrButton = ({ endpoint = '/api/expenses/ocr', onResult, onFilePicked, disabled }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pickedName, setPickedName] = useState('');
  const [pickedPreview, setPickedPreview] = useState('');
  const [cropFile, setCropFile] = useState(null);

  const onChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so the user can re-pick the same file later
    e.target.value = '';
    // Images go through the crop modal. PDFs skip the cropper (no warp).
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
      runOcr(file);
    } else {
      setCropFile(file);
    }
  };

  const runOcr = async (file) => {
    setPickedName(file.name);
    if (file.type.startsWith('image/')) {
      try { setPickedPreview(URL.createObjectURL(file)); } catch { /* ignore */ }
    } else {
      setPickedPreview('');
    }
    onFilePicked && onFilePicked(file);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}${endpoint}`, fd, { withCredentials: true });
      onResult && onResult(r.data || {});
      toast.success('Facture analysee — champs auto-remplis');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Echec de l\'analyse');
    } finally {
      setBusy(false);
    }
  };

  const handleCropConfirm = (warpedFile) => {
    setCropFile(null);
    runOcr(warpedFile);
  };

  const clearPicked = (e) => {
    e?.preventDefault();
    setPickedName('');
    setPickedPreview('');
    if (ref.current) ref.current.value = '';
    onFilePicked && onFilePicked(null);
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={disabled || busy}
        data-testid="receipt-ocr-btn"
        style={{
          padding: '8px 14px',
          background: 'linear-gradient(135deg, #1D4ED8, #7C3AED)',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          cursor: disabled || busy ? 'wait' : 'pointer',
          fontWeight: 700,
          fontSize: '0.84rem',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          boxShadow: '0 2px 6px rgba(124,58,237,0.25)',
        }}
      >
        {busy ? <Sparkle size={14} weight="fill" className="animate-pulse" /> : <Camera size={16} weight="bold" />}
        {busy ? 'Analyse Gemini...' : 'Photo de facture (auto)'}
      </button>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
        capture="environment"
        style={{ display: 'none' }}
        onChange={onChange}
        data-testid="receipt-ocr-input"
      />
      {pickedName && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: '#F4F5F7', fontSize: '0.78rem', color: '#0F172A' }}>
          {pickedPreview && <img src={pickedPreview} alt="" style={{ width: 22, height: 22, borderRadius: 4, objectFit: 'cover' }} />}
          {pickedName}
          <button type="button" onClick={clearPicked} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#5E5F66' }} aria-label="Retirer">
            <X size={12} weight="bold" />
          </button>
        </span>
      )}

      {cropFile && (
        <DocumentCropModal
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
};

export default ReceiptOcrButton;
